<template>
  <section class="bg-white py-space-md py-lg-space-xxl px-space-md px-lg-space-xxxl">
    <div class="container">
      <div class="row">
        <div class="col col-lg-10 offset-lg-1">
          <!-- title -->
          <h2 id="degree-detail" class="h2-medium mb-space-sm">{{ title }}</h2>
          <!-- description -->
          <div v-html="getDescription"></div>

          <!-- read more link -->
          <div v-if="showReadMore" class="text-center">
            <button
              class="btn btn-link text-decoration-underline show-text text-primary fs-medium p-0"
              @click="toggleReadMore"
            >
              Read more
            </button>
          </div>

          <!-- additional content -->
          <div v-show="showMoreText">
            <div v-html="getMoreDescription"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";

interface Props {
  title: string;
  description: string;
}

const props = defineProps<Props>();
const parsedDescription = ref<string[]>([]);
const splitIdx = ref<number>(5);
const showMoreText = ref(false);

const allowedDescriptionTags = new Set(["P", "UL", "OL", "DIV", "H2", "H3"]);
const allowedWrapperTags = new Set(["SECTION", "ARTICLE"]);
const headingTags = new Set(["H2", "H3"]);

const appendDescriptionElement = (el: Element, result: string[]) => {
  if (allowedDescriptionTags.has(el.tagName)) {
    result.push(el.outerHTML);

    if (result.length === 5 && headingTags.has(el.tagName)) {
      splitIdx.value = 4;
    }

    return;
  }

  if (allowedWrapperTags.has(el.tagName)) {
    for (let i = 0; i < el.children.length; i++) {
      appendDescriptionElement(el.children[i], result);
    }
  }
};

const setDescription = () => {
  const { description } = props;
  parsedDescription.value = [];
  splitIdx.value = 5;
  // init DOMParser
  const parser = new DOMParser();
  if (description) {
    // format description to by get rid of linebreak/tab/carriage-return
    const formattedDesc = description.replace(/(\r\n|\r\n\t|\n|\r|\t)/gm, "");
    // parse description text into DOM Parser
    const doc = parser.parseFromString(formattedDesc, "text/html");
    const result: string[] = [];
    const rootChildren = doc.body.children;
    for (let i = 0; i < rootChildren.length; i++) {
      appendDescriptionElement(rootChildren[i], result);
    }
    // set parsed result to ref
    parsedDescription.value = result;
  }
};

const getDescription = computed(() => {
  if (parsedDescription.value.length === 0) return "";
  return parsedDescription.value.slice(0, splitIdx.value).join("");
});

const showReadMore = computed(() => {
  return (
    parsedDescription.value.slice(0, parsedDescription.value.length - 1).length >= splitIdx.value &&
    !showMoreText.value
  );
});

const getMoreDescription = computed(() => {
  return parsedDescription.value.slice(splitIdx.value).join("");
});

const toggleReadMore = () => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    props.title.toLowerCase(),
    "read more",
    "details"
  );
  showMoreText.value = !showMoreText.value;
};
onMounted(() => {
  setDescription();
});
</script>
